/**
 * Log Service - Epic 38 - Story 38.1
 *
 * API client para sistema de logs de auditoria
 * Endpoints: /api/logs/audit
 *
 * Features:
 * - Multi-column sorting (até 3 níveis com Shift+Click)
 * - Filtros avançados (operação, entidade, usuário, data)
 * - Paginação configurável
 * - Export XLSX profissional
 * - JSON diff para alterações
 */

import apiClient from './apiClient';
import type {
  AuditLog,
  AuditLogListItem,
  AuditLogPaginatedResponse,
  AuditLogFilters,
  OperationOption,
  EntityCount,
  MultiSortConfig,
  ExportXlsxParams,
} from '../types/auditLog';

// ⛔ N217: sem isto o axios manda `operacao[]=DELETE` e o Django Ninja so le
// `operacao` -- os filtros Operacao/Entidade da tela e do Exportar XLSX eram
// ignorados em silencio. `indexes: null` gera `operacao=A&operacao=B`, o mesmo
// formato que o accessLogService ja usa.
export const AUDIT_PARAMS_SERIALIZER = { indexes: null };

class LogService {

  // ==========================================
  // LOGS DE AUDITORIA
  // ==========================================

  /**
   * Lista logs de auditoria com filtros, sorting e paginação
   *
   * @param filters - Filtros opcionais (operação, entidade, usuário, data)
   * @param sort - Multi-column sort config (até 3 níveis)
   * @param page - Número da página (default: 1)
   * @param pageSize - Tamanho da página (default: 50, max: 100)
   */
  async listAuditLogs(
    filters?: AuditLogFilters,
    sort?: MultiSortConfig,
    page: number = 1,
    pageSize: number = 50
  ): Promise<AuditLogPaginatedResponse> {
    // Build query params
    const params: Record<string, any> = {
      page,
      page_size: Math.min(pageSize, 100), // Max 100 por página
    };

    // Add filters
    if (filters?.operacao && filters.operacao.length > 0) {
      params.operacao = filters.operacao;
    }
    if (filters?.entidade && filters.entidade.length > 0) {
      params.entidade = filters.entidade;
    }
    if (filters?.usuario_search) params.usuario_search = filters.usuario_search;
    if (filters?.empresa) params.empresa = filters.empresa;
    if (filters?.data_inicio) params.data_inicio = filters.data_inicio;
    if (filters?.data_fim) params.data_fim = filters.data_fim;
    if (filters?.origem) params.origem = filters.origem; // S27.3 - Mobile/Web filter

    // Add multi-column sort
    if (sort && sort.length > 0) {
      params.sort = this.buildSortString(sort);
    }

    const response = await apiClient.get('/logs/audit', {
      params,
      paramsSerializer: AUDIT_PARAMS_SERIALIZER,
    });

    return response.data;
  }

  /**
   * Obtém detalhes completos de um log (com JSON diff)
   *
   * @param logId - ID do log
   */
  async getAuditLog(logId: number): Promise<AuditLog> {
    const response = await apiClient.get(`/logs/audit/${logId}`);
    return response.data;
  }

  /**
   * Lista operações disponíveis (CREATE, UPDATE, DELETE)
   */
  async getOperations(): Promise<OperationOption[]> {
    const response = await apiClient.get('/logs/audit/operations/list');
    return response.data;
  }

  /**
   * Lista entidades únicas com contagem de registros
   */
  async getEntities(): Promise<EntityCount[]> {
    const response = await apiClient.get('/logs/audit/entities/list');
    return response.data;
  }

  // ==========================================
  // EXPORT XLSX
  // ==========================================

  /**
   * Exporta logs para XLSX com formatação profissional
   *
   * @param filters - Filtros opcionais
   * @param sort - Multi-column sort config
   * @param limit - Limite de registros (default: 1000, max: 10000)
   */
  async exportXlsx(
    filters?: AuditLogFilters,
    sort?: MultiSortConfig,
    limit: number = 1000
  ): Promise<void> {
    // Build query params
    const params: Record<string, any> = {
      limit: Math.min(limit, 10000), // Max 10000 registros
    };

    // Add filters
    if (filters?.operacao && filters.operacao.length > 0) {
      params.operacao = filters.operacao;
    }
    if (filters?.entidade && filters.entidade.length > 0) {
      params.entidade = filters.entidade;
    }
    if (filters?.usuario_search) params.usuario_search = filters.usuario_search;
    if (filters?.empresa) params.empresa = filters.empresa;
    if (filters?.data_inicio) params.data_inicio = filters.data_inicio;
    if (filters?.data_fim) params.data_fim = filters.data_fim;
    if (filters?.origem) params.origem = filters.origem; // S27.3 - Mobile/Web filter

    // Add multi-column sort
    if (sort && sort.length > 0) {
      params.sort = this.buildSortString(sort);
    }

    // Request XLSX file
    const response = await apiClient.get('/logs/audit/export/xlsx', {
      params,
      paramsSerializer: AUDIT_PARAMS_SERIALIZER,
      responseType: 'blob',
    });

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'logs_auditoria.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Download file
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // ==========================================
  // HELPERS
  // ==========================================

  /**
   * Converte MultiSortConfig para string de query param
   *
   * Exemplo: [{key: 'data_hora', direction: 'desc'}, {key: 'operacao', direction: 'asc'}]
   *          → "data_hora:desc,operacao:asc"
   *
   * @param sortConfig - Array de configurações de ordenação (max 3 níveis)
   */
  private buildSortString(sortConfig: MultiSortConfig): string {
    return sortConfig
      .slice(0, 3) // Max 3 níveis
      .map((s) => `${s.key}:${s.direction}`)
      .join(',');
  }

  /**
   * Formata data/hora para exibição no fuso horário de São Paulo
   *
   * @param isoDate - Data em formato ISO (YYYY-MM-DDTHH:mm:ss)
   */
  formatDateTime(isoDate: string): string {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    return date.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  }

  /**
   * Formata data para exibição (sem hora) no fuso horário de São Paulo
   *
   * @param isoDate - Data em formato ISO (YYYY-MM-DD)
   */
  formatDate(isoDate: string): string {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    return date.toLocaleDateString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Trunca User Agent para exibição
   *
   * @param userAgent - User Agent string
   * @param maxLength - Comprimento máximo (default: 50)
   */
  truncateUserAgent(userAgent: string, maxLength: number = 50): string {
    if (!userAgent) return '';
    if (userAgent.length <= maxLength) return userAgent;
    return userAgent.substring(0, maxLength - 3) + '...';
  }

  /**
   * Extrai navegador do User Agent
   *
   * @param userAgent - User Agent string
   */
  getBrowserFromUserAgent(userAgent: string): string {
    if (!userAgent) return 'Desconhecido';

    if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) return 'Chrome';
    if (userAgent.includes('Edg/')) return 'Edge';
    if (userAgent.includes('Firefox/')) return 'Firefox';
    if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) return 'Safari';
    if (userAgent.includes('Opera/') || userAgent.includes('OPR/')) return 'Opera';

    return 'Outro';
  }
}

export default new LogService();
export { LogService };
export type {
  AuditLog,
  AuditLogListItem,
  AuditLogPaginatedResponse,
  AuditLogFilters,
  OperationOption,
  EntityCount,
  MultiSortConfig,
  ExportXlsxParams,
};
